import {
  collection,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase.config';
import baseProducts from '../assets/data/products';
import { getCustomProducts } from './adminService';

// In-memory cache for ultra-fast instantaneous UI rendering
let cachedMergedProducts = null;

// Deterministic random stock lookup so stock numbers remain stable across page refreshes
export const getStableRandomStock = (id) => {
  const seed = String(id || '1').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return ((seed * 13 + 7) % 25) + 4; // Yields a luxury boutique stock between 4 and 28
};

export const resolveStock = (p) => {
  if (p.stockQuantity !== undefined && p.stockQuantity !== null && !isNaN(Number(p.stockQuantity))) {
    return Math.max(0, Number(p.stockQuantity));
  }
  if (p.stock !== undefined && p.stock !== null && !isNaN(Number(p.stock))) {
    return Math.max(0, Number(p.stock));
  }
  return getStableRandomStock(p.id || 'piece-1');
};

/**
 * Merges base catalog, local custom products, and Firestore products into a unified list.
 */
export const mergeCatalog = (firestoreList = []) => {
  const localCustom = getCustomProducts();
  const map = new Map();

  // 1. Static base catalog
  baseProducts.forEach((p) => {
    const stockQty = resolveStock(p);
    map.set(p.id, {
      ...p,
      stockQuantity: stockQty,
      stock: stockQty,
      inStock: (p.inStock !== false) && stockQty > 0,
      price: Number(p.price) || 0,
    });
  });

  // 2. Local custom pieces created in Admin
  localCustom.forEach((p) => {
    const stockQty = resolveStock(p);
    map.set(p.id, {
      ...p,
      stockQuantity: stockQty,
      stock: stockQty,
      inStock: (p.inStock !== false) && stockQty > 0,
      price: Number(p.price) || 0,
    });
  });

  // 3. Cloud Firestore live pieces (highest authority)
  firestoreList.forEach((p) => {
    const stockQty = resolveStock(p);
    map.set(p.id, {
      ...p,
      stockQuantity: stockQty,
      stock: stockQty,
      inStock: (p.inStock !== false) && stockQty > 0,
      price: Number(p.price) || 0,
    });
  });

  const merged = Array.from(map.values());
  cachedMergedProducts = merged;
  return merged;
};

/**
 * Returns products immediately from memory/local cache, then updates if needed.
 */
export const getInitialProducts = () => {
  if (cachedMergedProducts && cachedMergedProducts.length > 0) {
    return cachedMergedProducts;
  }
  return mergeCatalog([]);
};

/**
 * Fetches the latest live product catalog from Firestore + local cache
 */
export const fetchStoreProducts = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    const firestoreProducts = [];
    snapshot.forEach((d) => {
      firestoreProducts.push({ id: d.id, ...d.data() });
    });
    return mergeCatalog(firestoreProducts);
  } catch (error) {
    console.warn('Could not fetch Firestore products catalog, using local fallback:', error);
    return mergeCatalog([]);
  }
};

/**
 * Finds a product by ID from cache or base catalog or Firestore
 */
export const getProductById = async (id) => {
  const initial = getInitialProducts();
  const found = initial.find((p) => p.id === id);
  if (found) return found;

  try {
    const docRef = doc(db, 'products', id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = { id: snap.id, ...snap.data() };
      return data;
    }
  } catch (e) {
    console.error('Error fetching single product:', e);
  }
  return null;
};

/**
 * Subscribes to real-time changes of products collection
 */
export const subscribeToProducts = (callback) => {
  // Fire immediately with initial cache
  callback(getInitialProducts());

  try {
    const productsRef = collection(db, 'products');
    const unsubscribe = onSnapshot(
      productsRef,
      (snapshot) => {
        const firestoreProducts = [];
        snapshot.forEach((d) => {
          firestoreProducts.push({ id: d.id, ...d.data() });
        });
        const merged = mergeCatalog(firestoreProducts);
        callback(merged);
      },
      (err) => {
        console.warn('Realtime product subscription notice:', err.message);
      }
    );

    // Also listen to local window events for immediate same-tab updates from Admin
    const handleLocalUpdate = () => {
      callback(mergeCatalog([]));
    };
    window.addEventListener('lhabilleur_products_updated', handleLocalUpdate);

    return () => {
      unsubscribe();
      window.removeEventListener('lhabilleur_products_updated', handleLocalUpdate);
    };
  } catch (err) {
    console.warn('Failed to setup Firestore onSnapshot for products:', err);
    return () => {};
  }
};

/**
 * Dispatches local event to immediately notify all product consumers in the app
 */
export const emitProductsUpdated = () => {
  try {
    window.dispatchEvent(new Event('lhabilleur_products_updated'));
  } catch (e) {
    // ignore
  }
};
